import * as React from "react";
import { useAppTheme } from "../../theme/AppThemeContext.jsx";
import { Box, ButtonBase, Typography } from "@mui/material";
import DayViewTimeline from "./DayViewTimeline";
import { useTasks } from "../../hooks/useTasks";

// ---------- Types (matching your existing Task model) ----------
export function CalendarDayStripWithTasks({
  user,
  weekStart,
  initialSelected,
  onTasksLoaded,
  spriteUrl,
  onToggleDone,
  onTaskClick,
  onAddTask,
}) {
  const [selectedDate, setSelectedDate] = React.useState(
    initialSelected ?? weekStart
  );
  const [tasks, setTasks] = React.useState([]);
  const [error, setError] = React.useState(null);

  // Handle adding a task from gap prompt
  const handleAddTask = React.useCallback((gapInfo) => {
    if (onAddTask) {
      onAddTask({
        ...gapInfo,
        date: selectedDate // Ensure the selected date is used
      });
    }
  }, [onAddTask, selectedDate]);

  // Update selectedDate when props change (e.g., when date picker is used)
  React.useEffect(() => {
    if (initialSelected) {
      setSelectedDate(initialSelected);
    }
  }, [initialSelected]);

  // Also update when weekStart changes
  React.useEffect(() => {
    if (!initialSelected) {
      setSelectedDate(weekStart);
    }
  }, [weekStart, initialSelected]);

  // Use your existing useTasks hook for consistency
  const { tasks: tasksFromHook, loading: hookLoading, error: hookError } = useTasks(user, selectedDate, { onAddTask: handleAddTask });

  // Sync tasks from hook to local state
  React.useEffect(() => {
    setTasks(tasksFromHook);
    setError(hookError);
    onTasksLoaded?.(tasksFromHook);
  }, [tasksFromHook, hookError, onTasksLoaded]);

  // Generate 7 days: Sun..Sat
  const days = React.useMemo(() => {
    const arr = [];
    const startOfWeek = weekStart instanceof Date && !Number.isNaN(weekStart.getTime()) 
      ? new Date(weekStart) 
      : new Date(); // Fallback to today if weekStart is invalid
    
    // Adjust to Sunday of current week
    const dayOfWeek = startOfWeek.getDay();
    const sunday = new Date(startOfWeek);
    sunday.setDate(startOfWeek.getDate() - dayOfWeek);
    
    for (let i = 0; i < 7; i++) {
      const day = new Date(sunday);
      day.setDate(sunday.getDate() + i);
      arr.push(day);
    }
    return arr;
  }, [weekStart]);

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      {/* Date strip (top panel) */}
      <Box
        sx={{
          px: { xs: 2, md: 4 },
          py: 1,
          display: "flex",
          justifyContent: "space-between",
          gap: 1,
          background: "rgb(248, 248, 248)",
          borderBottom: "1px solid rgba(0,0,0,0.06)",
          overflowX: "auto",
        }}
      >
        {(days ?? [])
          .filter((d) => d instanceof Date && !Number.isNaN(d.getTime()))
          .map((d) => {
            const isSelected = toDayKey(d) === toDayKey(selectedDate);
            return (
              <DayStripButton
                key={toDayKey(d)}
                date={d}
                selected={isSelected}
                onClick={() => setSelectedDate(d)}
              />
            );
          })}
      </Box>

      {/* Day View area (using your existing DayViewTimeline) */}
      <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
        {hookLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <Typography sx={{ color: 'text.secondary' }}>Loading tasks...</Typography>
          </Box>
        ) : error ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <Typography sx={{ color: 'error' }}>{error}</Typography>
          </Box>
        ) : tasks.length === 0 ? (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="h6" color="text.secondary">
              No tasks for this date.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Click "Add Task" to create one.
            </Typography>
          </Box>
        ) : (
          <DayViewTimeline 
            rows={tasks}
            spriteUrl={spriteUrl}
            onToggleDone={onToggleDone}
            onTaskClick={onTaskClick}
            containerBg="white"
          />
        )}
      </Box>
    </Box>
  );
}

// ---------- UI: single day item (Sun + date bubble) ----------
function DayStripButton({
  date,
  selected,
  onClick,
}) {
  const dow = date.toLocaleString(undefined, { weekday: "short" }); // "Sun"
  const dayNum = date.getDate(); // 8..14 etc
  const { tokens } = useAppTheme();

  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        minWidth: 90,
        px: 1,
        py: 0.75,
        borderRadius: 2,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 0.5,
        userSelect: "none",
        transition: "background-color 0.2s ease-in-out",
        "&:hover": {
          backgroundColor: "rgba(0,0,0,0.04)",
        },
      }}
    >
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 800,
          color: "rgba(0,0,0,0.40)",
        }}
      >
        {dow}
      </Typography>

      <Box
        sx={{
          width: 24,
          height: 24,
          borderRadius: "999px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: selected ? tokens.primary : "transparent",
          color: selected ? "white" : "rgba(0,0,0,0.75)",
          fontWeight: 900,
          fontSize: 12,
          border: selected ? `1px solid ${tokens.primary}` : "1px solid transparent",
          transition: "all 0.2s ease-in-out",
        }}
      >
        {dayNum}
      </Box>
    </ButtonBase>
  );
}

// ---------- Utilities ----------
function toDayKey(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
